// src/pages/Signup.tsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { MdCheckCircle } from "react-icons/md";
import { FaGoogle } from "react-icons/fa";
import { FiEye, FiEyeOff, FiUser, FiLock, FiMail } from "react-icons/fi";
import { Link } from "react-router-dom";
import styles from "./LoginDark.module.css";
import { useAuth } from "../contexts/AuthContext";
import authService from "../services/authService";
import InlineSpinner from "../components/common/InlineSpinner";
import { apiService } from "../services/apiService";
import { stashPendingClaim as stashPendingCollabClaim } from "../utils/pendingCollabClaim";

// Session storage keys for deduplication
const INVITE_PROCESSING_KEY = "portfolio_invite_processing";
const INVITE_PROCESSED_KEY = "portfolio_invite_completed";

// Cast react-icons for TypeScript
const CheckIcon = MdCheckCircle as React.FC<React.SVGProps<SVGSVGElement>>;
const GoogleIcon = FaGoogle as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeIcon = FiEye as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeOffIcon = FiEyeOff as React.FC<React.SVGProps<SVGSVGElement>>;
const UserIcon = FiUser as React.FC<React.SVGProps<SVGSVGElement>>;
const LockIcon = FiLock as React.FC<React.SVGProps<SVGSVGElement>>;
const MailIcon = FiMail as React.FC<React.SVGProps<SVGSVGElement>>;

type ProviderType = "google" | "microsoft";

interface SignupProps {
  isVCInvite?: boolean;
}

const Signup: React.FC<SignupProps> = ({ isVCInvite = false }) => {
  const {
    signInWithProvider,
    signInWithGoogle,
    requestEmailSignupOtp,
    error,
    confirmationEmailSent,
    user,
  } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();
  const { vcSlug } = useParams<{ vcSlug: string }>();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [emailLoading, setEmailLoading] = useState<boolean>(false);
  const [registrationSuccess, setRegistrationSuccess] =
    useState<boolean>(false);
  const [providerLoading, setProviderLoading] = useState<ProviderType | null>(
    null
  );
  const [signupError, setSignupError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Ref to prevent duplicate invite processing
  const inviteProcessingRef = useRef<boolean>(false);

  // Resend confirmation email state
  const [resendTimer, setResendTimer] = useState<number>(30);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [resendSuccess, setResendSuccess] = useState<boolean>(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // Portfolio invite state (email-based)
  const [portfolioInvite, setPortfolioInvite] = useState<{
    valid: boolean;
    email?: string;
    companyName?: string;
    inviteId?: string;
    vcName?: string;
    requiredCategories?: string[];
  } | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteProcessed, setInviteProcessed] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // VC invite slug state (universal link)
  const [vcInviteData, setVcInviteData] = useState<{
    valid: boolean;
    vcId?: string;
    fundName?: string;
    requiredCategories?: string[];
    error?: string;
  } | null>(null);
  const [vcInviteLoading, setVcInviteLoading] = useState(false);

  // Check for promo code in URL on mount
  useEffect(() => {
    const promoCode = searchParams.get("promo");
    if (promoCode) {
      localStorage.setItem("promo_code", promoCode.trim());
    }
  }, [searchParams]);

  // Check for portfolio invite on mount with deduplication
  useEffect(() => {
    const inviteToken = searchParams.get("portfolio_invite");
    if (!inviteToken) return;

    // Check if this invite was already completed in another tab
    const completedInvite = sessionStorage.getItem(INVITE_PROCESSED_KEY);
    if (completedInvite === inviteToken) {
      console.log("[Signup] Invite already processed in this session, redirecting to dashboard");
      // Use replace to prevent back button issues
      window.location.replace("/");
      return;
    }

    // Check if another tab is currently processing this invite
    const processingInvite = sessionStorage.getItem(INVITE_PROCESSING_KEY);
    if (processingInvite === inviteToken && !inviteProcessingRef.current) {
      console.log("[Signup] Invite is being processed in another tab");
      // Show a message or redirect
      setInviteError("This invitation is being processed in another tab. Please check that tab or refresh this page.");
      return;
    }

    // Mark this invite as being processed by this tab
    sessionStorage.setItem(INVITE_PROCESSING_KEY, inviteToken);
    inviteProcessingRef.current = true;

    validatePortfolioInvite(inviteToken);
  }, [searchParams]);

  // Fundraise collaborator invite: stash the token so the pending-claim
  // modal can resurface it after auth resolves. Lives in localStorage so
  // it survives email confirmation → new tab → first sign-in.
  useEffect(() => {
    const claimToken = searchParams.get("claim");
    if (claimToken) {
      stashPendingCollabClaim(claimToken);
    }
  }, [searchParams]);

  // Check for VC slug invite on mount
  useEffect(() => {
    if (isVCInvite && vcSlug) {
      validateVCSlug(vcSlug);
    }
  }, [isVCInvite, vcSlug]);

  // Accept invite after successful signup
  useEffect(() => {
    if (
      user &&
      portfolioInvite?.valid &&
      portfolioInvite.inviteId &&
      !inviteProcessed
    ) {
      // Normalize emails for comparison (matching backend logic)
      const normalize = (e: string) => (e || "").toLowerCase().trim().replace(/\s+/g, "");
      const userEmail = normalize(user.email || "");
      const inviteEmail = normalize(portfolioInvite.email || "");
      
      // Only auto-accept if the emails match exactly
      if (userEmail === inviteEmail) {
        acceptPortfolioInvite();
      } else {
        // Log mismatch and show a helpful error instead of calling the API and getting a 400
        console.warn("Auto-accept skipped due to email mismatch:", { 
          user: userEmail, 
          invite: inviteEmail 
        });
        
        // Only set the error if it hasn't been set yet to avoid potential loops
        if (!inviteError && !inviteProcessed) {
          setInviteError(
            `You are logged in as ${user.email}, but this invitation is for ${portfolioInvite.email}. Please sign out and use the correct account.`
          );
        }
      }
    }
  }, [user, portfolioInvite, inviteProcessed, inviteError]);

  // Start countdown timer when confirmation email is sent or resent
  useEffect(() => {
    if ((confirmationEmailSent || registrationSuccess) && !canResend) {
      const interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [confirmationEmailSent, registrationSuccess, canResend]);

  const validatePortfolioInvite = async (token: string) => {
    setInviteLoading(true);
    try {
      const response = await apiService.validatePortfolioInvite(token);
      if (response.valid) {
        setPortfolioInvite(response);
        // Pre-fill email (locked) - normalize to lowercase and trim whitespace
        if (response.email) {
          const normalizedEmail = response.email.trim().toLowerCase();
          setEmail(normalizedEmail);
        }

        // Store complete portfolio invite data in localStorage for onboarding
        localStorage.setItem(
          "portfolio_invite_data",
          JSON.stringify({
            inviteId: response.inviteId,
            vcName: response.vcName,
            vcEmail: response.vcEmail || '',
            companyName: response.companyName,
            isVCInvite: true,
            requiredCategories: response.requiredCategories || [],
          })
        );

        // Store required categories separately for easy access during onboarding
        if (response.requiredCategories && response.requiredCategories.length > 0) {
          localStorage.setItem(
            "vc_required_categories",
            JSON.stringify(response.requiredCategories)
          );
        }
      } else {
        console.error("Invalid portfolio invite:", response.error);
      }
    } catch (err) {
      console.error("Error validating portfolio invite:", err);
    } finally {
      setInviteLoading(false);
    }
  };

  const validateVCSlug = async (slug: string) => {
    setVcInviteLoading(true);
    try {
      const API_BASE_URL =
        process.env.REACT_APP_API_URL ||
        (window.location.hostname === "localhost"
          ? "http://localhost:4000"
          : "https://api.sendupdates.ai");

      const response = await fetch(
        `${API_BASE_URL}/api/vc-invite-slug/validate/${slug}`
      );
      const data = await response.json();
      setVcInviteData(data);
    } catch (err) {
      setVcInviteData({
        valid: false,
        error: "Failed to validate invite link",
      });
    } finally {
      setVcInviteLoading(false);
    }
  };

  const acceptPortfolioInvite = async () => {
    if (!portfolioInvite?.inviteId) return;

    // Prevent duplicate calls
    if (inviteProcessed) {
      console.log("[Signup] Invite already processed, skipping");
      return;
    }

    try {
      setInviteError(null); // Clear any previous errors

      // Log for debugging
      console.log("[Signup] Accepting portfolio invite:", {
        inviteId: portfolioInvite.inviteId,
        inviteEmail: portfolioInvite.email,
        currentUserEmail: user?.email,
      });

      await apiService.acceptPortfolioInvite(portfolioInvite.inviteId);
      console.log("[Signup] Portfolio invite accepted successfully");

      // Mark as processed in session storage to prevent other tabs from processing
      sessionStorage.setItem(INVITE_PROCESSED_KEY, portfolioInvite.inviteId);
      // Clear the processing flag
      sessionStorage.removeItem(INVITE_PROCESSING_KEY);

      // Set local state
      setInviteProcessed(true);

      // Clear the portfolio_invite param from URL to prevent re-processing on refresh
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete("portfolio_invite");
      setSearchParams(newSearchParams, { replace: true });

      // Use window.location.replace for clean navigation without history entry
      // This prevents back button from returning to signup page
      setTimeout(() => {
        window.location.replace("/");
      }, 1500);
    } catch (err) {
      console.error("[Signup] Error accepting portfolio invite:", err);

      // Set error state to display to user
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to accept portfolio invitation. Please try again.";
      setInviteError(errorMessage);

      // Clear processing flag on error so user can retry
      sessionStorage.removeItem(INVITE_PROCESSING_KEY);

      // Do NOT set inviteProcessed to true on error
      // This allows the useEffect to retry if needed
    }
  };

  const handleSocialLogin = async (provider: ProviderType) => {
    setProviderLoading(provider);
    try {
      // Store portfolio invite token before OAuth redirect so we can resume after
      const inviteToken = searchParams.get("portfolio_invite");
      if (inviteToken) {
        sessionStorage.setItem("pending_portfolio_invite", inviteToken);
      }

      // Store promo code before OAuth redirect so we can restore it after
      const promoCode = searchParams.get("promo") || localStorage.getItem("promo_code");
      if (promoCode) {
        sessionStorage.setItem("pending_promo_code", promoCode);
      }

      if (provider === "google") {
        await signInWithGoogle();
      } else {
        await signInWithProvider(provider);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setProviderLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSignupError(null); // Clear any previous errors

    // Validate email
    if (!email || !email.trim()) {
      setSignupError("Email is required");
      return;
    }

    // Validate password
    if (!password || password.length < 8) {
      setSignupError("Password must be at least 8 characters");
      return;
    }

    setEmailLoading(true);

    try {
      // For portfolio invites, use the exact email from the invite validation
      // This ensures we use the exact email format that was validated
      let emailToUse = email.trim().toLowerCase();
      if (portfolioInvite?.valid && portfolioInvite.email) {
        // Use the exact email from the invite validation response
        emailToUse = portfolioInvite.email.trim().toLowerCase();
        console.log("Using invite email for registration:", {
          inviteEmail: portfolioInvite.email,
          normalizedEmail: emailToUse,
        });
      }

      const userResult = await authService.register({
        email: emailToUse,
        password,
        fullName: "",
      });
      setRegistrationSuccess(true);

      // If universal VC invite, create portfolio relationship
      if (
        isVCInvite &&
        vcInviteData?.valid &&
        vcInviteData.vcId &&
        userResult?.id
      ) {
        await createPortfolioRelationshipFromSlug(
          vcInviteData.vcId,
          userResult.id,
          emailToUse
        );

        // Store required integrations in localStorage for onboarding
        if (vcInviteData.requiredCategories) {
          localStorage.setItem(
            "vc_required_categories",
            JSON.stringify(vcInviteData.requiredCategories)
          );
        }
      }
    } catch (err) {
      console.error("Signup error:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to sign up. Please try again.";
      setSignupError(errorMessage);
    } finally {
      setEmailLoading(false);
    }
  };

  const createPortfolioRelationshipFromSlug = async (
    vcId: string,
    userId: string,
    userEmail: string
  ) => {
    try {
      await apiService.createPortfolioRelationshipFromSlug(
        vcId,
        userId,
        userEmail
      );
      console.log("Portfolio relationship created successfully");
    } catch (err) {
      console.error("Error creating portfolio relationship:", err);
    }
  };

  const handleResendConfirmation = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setResendError(null);

    try {
      // For portfolio invites, use the exact email from the invite validation
      let emailToUse = email.trim().toLowerCase();
      if (portfolioInvite?.valid && portfolioInvite.email) {
        // Use the exact email from the invite validation response
        emailToUse = portfolioInvite.email.trim().toLowerCase();
      }
      
      if (password && password.length >= 8) {
        // For email/password signups, re-register (this resends confirmation)
        await authService.register({ email: emailToUse, password, fullName: "" });
      } else {
        // For OTP-based signups, request new OTP
        await requestEmailSignupOtp(emailToUse);
      }

      setResendSuccess(true);
      // Reset timer to 30 seconds
      setCanResend(false);
      setResendTimer(30);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 3000);
    } catch (err) {
      console.error("Error resending confirmation:", err);
      setResendError(
        err instanceof Error
          ? err.message
          : "Failed to resend confirmation email"
      );
      // Clear error after 5 seconds
      setTimeout(() => {
        setResendError(null);
      }, 5000);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.glowDots} aria-hidden="true">
        <div
          className={`${styles.dot} ${styles.dotBlue}`}
          style={{ top: "8%", left: "12%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotPurple}`}
          style={{ top: "20%", right: "10%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotYellow}`}
          style={{ bottom: "18%", left: "22%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotRed}`}
          style={{ bottom: "10%", right: "18%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotTeal}`}
          style={{ top: "40%", left: "8%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotPink}`}
          style={{ top: "65%", right: "8%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotYellow}`}
          style={{ top: "32%", left: "45%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotPurple}`}
          style={{ bottom: "28%", right: "38%" }}
        />
      </div>

      <div className={styles.inner}>
        <div className={styles.pulseContainer} aria-hidden="true">
          <div className={styles.pulseCircle}></div>
        </div>
        <h1 className={styles.title}>
          {vcInviteData?.valid
            ? `Join ${vcInviteData.fundName}'s Portfolio`
            : portfolioInvite?.valid
            ? `Join ${portfolioInvite.companyName || "your company"}`
            : "Sign up for Sendupdates"}
        </h1>

        {/* VC Universal Invite Banner */}
        {vcInviteData?.valid && (
          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "1.25rem 1.5rem",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              textAlign: "center",
              color: "white",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
          >
            <p style={{ margin: 0, fontSize: "1rem", fontWeight: 700 }}>
              You&apos;ve been invited by{" "}
              <strong>{vcInviteData.fundName}</strong> to join SendUpdates.ai
            </p>
            {vcInviteData.requiredCategories &&
              vcInviteData.requiredCategories.length > 0 && (
                <p
                  style={{
                    margin: "0.5rem 0 0 0",
                    fontSize: "0.875rem",
                    opacity: 0.9,
                    fontWeight: 500,
                  }}
                >
                  You&apos;ll be asked to connect integrations from{" "}
                  {vcInviteData.requiredCategories.length} required{" "}
                  categor{vcInviteData.requiredCategories.length !== 1 ? "ies" : "y"}{" "}
                  during onboarding
                </p>
              )}
          </div>
        )}

        {/* Portfolio Invite Banner */}
        {portfolioInvite?.valid && (
          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "1.25rem 1.5rem",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              textAlign: "center",
              color: "white",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "1.0625rem",
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              <strong>{portfolioInvite.vcName}</strong> has invited you to join
              SendUpdates.ai
            </p>
            {portfolioInvite.companyName && (
              <p
                style={{
                  margin: "0.625rem 0 0 0",
                  fontSize: "0.9375rem",
                  opacity: 0.95,
                  fontWeight: 500,
                }}
              >
                Company: <strong>{portfolioInvite.companyName}</strong>
              </p>
            )}
          </div>
        )}

        {/* Portfolio Invite Error Banner */}
        {inviteError && (
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #ef4444",
              padding: "1rem 1.5rem",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              textAlign: "center",
              color: "#991b1b",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
            role="alert"
            aria-live="assertive"
          >
            <p
              style={{
                margin: 0,
                fontSize: "0.9375rem",
                fontWeight: 600,
              }}
            >
              {inviteError}
            </p>
          </div>
        )}

        <div className={styles.card} role="region" aria-label="Signup form">
          {inviteLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "2rem",
              }}
            >
              <InlineSpinner />
              <span style={{ marginLeft: "0.5rem" }}>
                Validating invitation...
              </span>
            </div>
          ) : confirmationEmailSent || registrationSuccess ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "1rem",
              }}
              role="alert"
              aria-live="polite"
            >
              <CheckIcon
                style={{
                  fontSize: "2rem",
                  color: "#10B981",
                  marginBottom: "0.5rem",
                }}
                aria-hidden="true"
              />
              <p style={{ fontSize: "1rem", textAlign: "center" }}>
                A confirmation email has been sent to your email address. Please
                check your inbox to confirm and then sign in with your password.
              </p>

              {/* Resend button */}
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={!canResend || resendLoading}
                className={styles.resendButton}
                aria-label={
                  canResend
                    ? "Resend confirmation email"
                    : `Resend available in ${resendTimer} seconds`
                }
              >
                {resendLoading ? (
                  <>
                    <InlineSpinner />
                    Sending...
                  </>
                ) : (
                  <>
                    <MailIcon style={{ fontSize: "1rem" }} />
                    {canResend
                      ? "Resend Confirmation Email"
                      : `Resend in ${resendTimer}s`}
                  </>
                )}
              </button>

              {/* Success/Error feedback */}
              {resendSuccess && (
                <div className={styles.resendSuccess}>
                  <CheckIcon style={{ fontSize: "1rem" }} />
                  Email sent successfully!
                </div>
              )}
              {resendError && (
                <div className={styles.resendError}>{resendError}</div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  Email
                </label>
                <div className={styles.inputWrapper}>
                  <UserIcon
                    className={styles.inputIconLeft}
                    aria-hidden="true"
                  />
                  <input
                    id="email"
                    type="email"
                    placeholder="Email"
                    className={styles.input}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={
                      emailLoading ||
                      (portfolioInvite?.valid && !!portfolioInvite.email)
                    }
                    autoComplete="email"
                    aria-label="Email address"
                    required
                    readOnly={portfolioInvite?.valid && !!portfolioInvite.email}
                    style={
                      portfolioInvite?.valid && portfolioInvite.email
                        ? { backgroundColor: "#262626", cursor: "not-allowed", color: "#fff" }
                        : undefined
                    }
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.label}>
                  Password
                </label>
                <div className={styles.inputWrapper}>
                  <LockIcon
                    className={styles.inputIconLeft}
                    aria-hidden="true"
                  />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    className={styles.input}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={emailLoading}
                    autoComplete="new-password"
                    aria-label="Password"
                  />
                  <button
                    type="button"
                    className={styles.inputIconRight}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowPassword((s) => !s)}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className={styles.primaryButton}
                disabled={emailLoading || providerLoading !== null}
                aria-label="Sign up"
              >
                Sign Up
                {emailLoading && <InlineSpinner />}
              </button>

              {(error || signupError) && (
                <div className={styles.error}>{signupError || error}</div>
              )}

              <div className={styles.divider} aria-hidden="true">
                <span className={styles.dividerLine}></span>
                <span className={styles.dividerText}>Or Sign up With</span>
                <span className={styles.dividerLine}></span>
              </div>

              <div className={styles.socialRow}>
                <button
                  type="button"
                  aria-label="Sign up with Google"
                  className={`${styles.socialButton} ${styles.googleButton}`}
                  onClick={() => handleSocialLogin("google")}
                  disabled={providerLoading !== null || emailLoading}
                >
                  <GoogleIcon className={styles.socialIcon} />
                  Google
                  {providerLoading === "google" && <InlineSpinner />}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className={styles.footer}>
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
